import type { PdfCompanyInfo } from '@/components/documents/invoices/InvoicePDF';
import { useBillingSettings } from '@/hooks/settings/billing/useBillingSettings';
import { useCompanySettings } from '@/hooks/settings/useCompanySettings';

/**
 * The company block an invoice PDF prints, from company and billing settings —
 * shared so every place that renders an invoice PDF prints the same letterhead.
 */
export function useInvoicePdfCompany(): PdfCompanyInfo | null {
  const { data: companySettings } = useCompanySettings();
  const { data: billingSettings } = useBillingSettings();
  if (!companySettings) return null;
  return {
    name: companySettings.name,
    contractorLicense: companySettings.contractorLicense ?? null,
    regulatoryNotice: companySettings.regulatoryNotice ?? null,
    logoUrl: billingSettings?.invoices.branding.logoUrl ?? companySettings.logoUrl ?? null,
    primaryColor: billingSettings?.invoices.branding.primaryColor ?? null,
    invoiceFooterText: billingSettings?.invoices.footerText ?? null,
    address:
      [
        companySettings.address?.street,
        companySettings.address?.city,
        companySettings.address?.state,
        companySettings.address?.zip,
      ]
        .filter(Boolean)
        .join(', ') || null,
    // The design invoice letterhead turns these on by default, so the
    // back-office download must carry them or it loses company contact info
    // the hosted page and public PDF both show.
    email: companySettings.email ?? null,
    phone: companySettings.phone ?? null,
    website: companySettings.website ?? null,
    // NOT-7 — surface the company's tax registration number on the
    // customer-facing invoice PDF (mirrors the share-link view).
    taxRegistrationNumber: companySettings.taxRegistrationNumber ?? null,
  };
}
